import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import VehicleCard from "@/components/VehicleCard";
import BookingDialog from "@/components/BookingDialog";
import api from "@/lib/api";
import { SEGMENTS, FUELS } from "@/lib/site";

const SEATS_OPTS = [2, 4, 5, 7];
const LUGGAGE_OPTS = [1, 2, 3];

function FilterPanel({ filters, setFilters }) {
  const toggle = (key, value) => {
    setFilters((f) => ({ ...f, [key]: f[key] === value ? "" : value }));
  };
  return (
    <div className="space-y-6">
      <div>
        <h4 className="font-semibold text-[#171717] mb-3">Araç Sınıfı</h4>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700">
            <Checkbox checked={!filters.segment} onCheckedChange={() => setFilters((f) => ({ ...f, segment: "" }))} data-testid="filter-segment-all" />
            Tümü
          </label>
          {SEGMENTS.map((s) => (
            <label key={s.value} className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700">
              <Checkbox checked={filters.segment === s.value} onCheckedChange={() => toggle("segment", s.value)} data-testid={`filter-segment-${s.value}`} />
              {s.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-[#171717] mb-3">Yakıt Türü</h4>
        <div className="space-y-2">
          {FUELS.map((f) => (
            <label key={f.value} className="flex items-center gap-2 cursor-pointer text-sm text-neutral-700">
              <Checkbox checked={filters.fuel === f.value} onCheckedChange={() => toggle("fuel", f.value)} data-testid={`filter-fuel-${f.value}`} />
              {f.label}
            </label>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-[#171717] mb-3">Yolcu Sayısı</h4>
        <div className="flex flex-wrap gap-2">
          {SEATS_OPTS.map((n) => (
            <button key={n} onClick={() => toggle("seats", n)} data-testid={`filter-seats-${n}`}
              className={`px-3 py-1.5 rounded-lg text-sm border ${filters.seats === n ? "border-[#A11016] bg-[#FFF0F2] text-[#A11016]" : "border-neutral-200 text-neutral-600"}`}>{n}+</button>
          ))}
        </div>
      </div>
      <div>
        <h4 className="font-semibold text-[#171717] mb-3">Bagaj Kapasitesi</h4>
        <div className="flex flex-wrap gap-2">
          {LUGGAGE_OPTS.map((n) => (
            <button key={n} onClick={() => toggle("luggage", n)} data-testid={`filter-luggage-${n}`}
              className={`px-3 py-1.5 rounded-lg text-sm border ${filters.luggage === n ? "border-[#A11016] bg-[#FFF0F2] text-[#A11016]" : "border-neutral-200 text-neutral-600"}`}>{n}+</button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Vehicles() {
  const [searchParams] = useSearchParams();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("order");
  const [booking, setBooking] = useState(null);
  const [filters, setFilters] = useState({
    segment: searchParams.get("segment") || "",
    fuel: "", seats: 0, luggage: 0,
  });

  const initialSearch = {
    pickup: searchParams.get("pickup") || "Taksim",
    dropoff: searchParams.get("dropoff") || searchParams.get("pickup") || "Taksim",
    pickup_dt: searchParams.get("pickup_dt") || "",
    return_dt: searchParams.get("return_dt") || "",
  };

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    const params = { sort };
    if (filters.segment) params.segment = filters.segment;
    if (filters.fuel) params.fuel = filters.fuel;
    if (filters.seats) params.seats = filters.seats;
    if (filters.luggage) params.luggage = filters.luggage;
    try {
      const res = await api.get("/vehicles", { params });
      setVehicles(res.data);
    } catch {}
    setLoading(false);
  }, [sort, filters]);

  useEffect(() => { document.title = "Araçlarımız | VoyaCar Rental"; }, []);
  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const clearFilters = () => setFilters({ segment: "", fuel: "", seats: 0, luggage: 0 });

  return (
    <div>
      <div className="bg-[#171717] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl sm:text-4xl font-bold">Araçlarımız</h1>
          <p className="mt-2 text-neutral-300">İhtiyacınıza en uygun aracı seçin, hemen talep oluşturun.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <aside className="hidden lg:block">
            <div className="bg-white rounded-2xl border border-neutral-200 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-[#171717] flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-[#A11016]" /> Filtreler</h3>
                <button onClick={clearFilters} className="text-xs text-[#A11016] hover:underline" data-testid="clear-filters-button">Temizle</button>
              </div>
              <FilterPanel filters={filters} setFilters={setFilters} />
            </div>
          </aside>

          <div className="lg:col-span-3">
            <div className="flex items-center justify-between gap-3 mb-6">
              <p className="text-sm text-neutral-500" data-testid="vehicle-count">{loading ? "Yükleniyor..." : `${vehicles.length} araç bulundu`}</p>
              <div className="flex items-center gap-2">
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden gap-2" data-testid="mobile-filter-trigger"><SlidersHorizontal className="h-4 w-4" /> Filtre</Button>
                  </SheetTrigger>
                  <SheetContent side="left" className="w-[85%] overflow-y-auto">
                    <div className="flex items-center justify-between mb-5 mt-2">
                      <h3 className="font-bold text-[#171717]">Filtreler</h3>
                      <button onClick={clearFilters} className="text-xs text-[#A11016]">Temizle</button>
                    </div>
                    <FilterPanel filters={filters} setFilters={setFilters} />
                  </SheetContent>
                </Sheet>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[180px]" data-testid="sort-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="order">Önerilen Sıralama</SelectItem>
                    <SelectItem value="popular">En Popüler</SelectItem>
                    <SelectItem value="name">Araç Adı</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!loading && vehicles.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-neutral-200">
                <X className="h-10 w-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500">Seçtiğiniz kriterlere uygun araç bulunamadı.</p>
                <Button variant="outline" className="mt-4" onClick={clearFilters}>Filtreleri Temizle</Button>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {vehicles.map((v, i) => <VehicleCard key={v.id} vehicle={v} index={i} onBook={setBooking} />)}
            </div>
          </div>
        </div>
      </div>

      <BookingDialog open={!!booking} onOpenChange={(o) => !o && setBooking(null)} vehicle={booking} initial={initialSearch} />
    </div>
  );
}
